import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useLiveChatSDK } from '@inno-sdk/livechat-sdk'
import { AppId, emitEventOnIframe } from '@inno-tools/container-bridge'
import cn from 'classnames'
import { Variants, motion, useDragControls } from 'motion/react'
import { MAX_Z_INDEX } from 'shared/constants/apps'
import { useAnalyticsFrame } from 'shared/hooks/analytics/useAnalyticsFrame'
import { useMediaQuery } from 'shared/hooks/useMediaQuery'
import { WindowState } from 'shared/types'
import { generateWindowIframeId } from 'shared/utils/generateWindowIframeId'

import { useWindowStore } from '../../../../stores'
import styles from './Window.module.scss'
import {
  NavigationControls,
  SnapOverlay,
  WindowContent,
  WindowControls
} from './components'
import { normalizeUrl } from './components/WindowContent/utils'
import { useWindowUrl } from './components/WindowControls/useWindowUrl'
import { useHistoryManagement } from './hooks/useHistoryManagement'
import { resolveMountedIframeSrc } from './hooks/useHistoryManagement.utils'
import { useInitWindowSnappingShortcuts } from './hooks/useInitWindowSnappingShortcuts'
import { useInjectIframeScript } from './hooks/useInjectIframeScript'
import { useListenSpeechToText } from './hooks/useListenSpeechToText'
import { useTicketCRUDListener } from './hooks/useTicketCRUDListener'
import { useWindowDrag } from './hooks/useWindowDrag'
import {
  WindowVariant,
  useGetCurrentWindowVariant,
  useWindowDimensions,
  useWindowVariants
} from './windowVariants'

interface WindowProps {
  window: WindowState
  constraintsRef: React.RefObject<HTMLDivElement>
  topDockHeight: number
}

const Window: React.FC<WindowProps> = ({
  window,
  constraintsRef,
  topDockHeight
}) => {
  const windowRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const iframeId = generateWindowIframeId(window.id)
  const { isDesktop } = useMediaQuery()

  const minimizeWindow = useWindowStore(state => state.minimizeWindow)
  const maximizeWindow = useWindowStore(state => state.maximizeWindow)
  const closeWindow = useWindowStore(state => state.closeWindow)
  const updateWindowPosition = useWindowStore(
    state => state.updateWindowPosition
  )
  const windowUrl = useWindowUrl(window.id)

  const { livechat } = useLiveChatSDK()

  const dragControls = useDragControls()
  const [isDragging, setIsDragging] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const isAnimating = useRef(false)
  const isAppReady = useRef(false)

  const activeWindowId = useWindowStore(state => state.activeWindowId)
  const isActiveWindow = activeWindowId === window.id
  useAnalyticsFrame(iframeRef, {
    id: window.id,
    title: window.title,
    visible: window.isVisible && !window.isMinimized,
    active: isActiveWindow
  })
  const setActiveWindow = useWindowStore(state => state.setActiveWindow)

  const commitWindowPosition = useCallback(
    ({ clamp = true }: { clamp?: boolean } = {}) => {
      const rect = windowRef.current?.getBoundingClientRect()
      if (!rect) {
        return
      }

      const maxX = Math.max(0, globalThis.window.innerWidth - rect.width)
      const minX = 0

      const maxY = Math.max(0, globalThis.window.innerHeight - rect.height)
      const minY = constraintsRef.current?.offsetTop || 0

      const nonClampedPosition = {
        x: Math.floor(rect.x),
        y: Math.floor(rect.y)
      }

      const clampedPosition = {
        x: Math.max(minX, Math.min(Math.floor(rect.x), maxX)),
        y: Math.max(minY, Math.min(Math.floor(rect.y), maxY))
      }

      updateWindowPosition(window.id, nonClampedPosition)

      if (clamp) {
        requestAnimationFrame(() =>
          updateWindowPosition(window.id, clampedPosition)
        )
      }
    },
    [constraintsRef, updateWindowPosition, window.id]
  )

  const handleDragStart = useCallback(() => {
    setActiveWindow(window.id)
  }, [setActiveWindow, window.id])

  const handleClose = useCallback(() => {
    const isIframeWindow = !!window.url && !window.localComponent

    if (!isIframeWindow || !isAppReady.current) {
      closeWindow(window.id)
      return
    }

    minimizeWindow(window.id)

    iframeRef.current?.contentWindow?.postMessage(
      { type: 'UNMOUNT_APP', data: { windowId: window.id } },
      globalThis.window.location.origin
    )
  }, [
    window.id,
    window.url,
    window.localComponent,
    closeWindow,
    minimizeWindow
  ])

  const handleMinimize = useCallback(() => {
    minimizeWindow(window.id)
    emitEventOnIframe(
      { type: 'MINIMIZE_WINDOW', data: { windowId: window.id } },
      iframeRef.current as HTMLIFrameElement
    )
  }, [minimizeWindow, window.id])

  const handleMaximize = useCallback(() => {
    maximizeWindow(window.id)
    emitEventOnIframe(
      { type: 'MAXIMIZE_WINDOW', data: { windowId: window.id } },
      iframeRef.current as HTMLIFrameElement
    )
  }, [maximizeWindow, window.id])

  const handleReload = useCallback(() => {
    if (!iframeRef.current?.src || !windowUrl) return

    const normalizedTargetUrl = normalizeUrl(windowUrl)
    const normalizedCurrentSrc = normalizeUrl(iframeRef.current.src)

    if (normalizedCurrentSrc === normalizedTargetUrl) {
      iframeRef.current.contentWindow?.location.reload()
    } else {
      iframeRef.current.src = normalizedTargetUrl
    }
  }, [windowUrl])

  const {
    snapPreviewRect,
    dragOrigin,
    handleMotionDragStart,
    handleMotionDrag,
    handleMotionDragEnd,
    handleMotionTransitionEnd,
    handleTitleBarMouseDown,
    getSnapRect
  } = useWindowDrag({
    iframeRef,
    constraintsRef,
    windowState: window,
    controls: dragControls,
    onBeginDrag: handleDragStart,
    onCommitPosition: commitWindowPosition,
    isDragging,
    setDragging: setIsDragging,
    snapEnabled: window.canSnap,
    dragEnabled: isDesktop
  })

  const { hasBack, hasForward, handleBack, handleForward, currentUrl } =
    useHistoryManagement(iframeRef, { iframeId, window })
  const mountedIframeSrcRef = useRef<string | undefined>(undefined)

  if (mountedIframeSrcRef.current === undefined) {
    mountedIframeSrcRef.current = currentUrl
  }

  const iframeSrc = resolveMountedIframeSrc({
    mountedSrc: mountedIframeSrcRef.current,
    nextRequestedSrc: currentUrl
  })

  const { variant: windowVariant } = useGetCurrentWindowVariant(window)

  // Animation variants handle position, opacity, scale, and display
  const variants: Variants = useWindowVariants({
    window,
    initialPosition: window.position,
    constraintsRef,
    getSnapRect,
    topDockHeight
  })

  // Dimensions animated with layout prop for better performance
  const dimensions = useWindowDimensions({
    window,
    windowVariant,
    getSnapRect
  })

  const isIframe = !!window.url && !window.localComponent

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (
        event.data?.type === 'APP_READY' &&
        event.data?.windowId === window.id
      ) {
        isAppReady.current = true
      }
    }
    globalThis.window.addEventListener('message', onMessage)
    return () => {
      globalThis.window.removeEventListener('message', onMessage)
    }
  }, [window.id])

  useEffect(() => {
    if (window.id !== AppId.LIVE_CHAT) {
      return
    }

    return () => {
      const isStillOpen = !!useWindowStore
        .getState()
        .getWindowState(AppId.LIVE_CHAT)

      if (isStillOpen) {
        return
      }
      // Leave all subscribed LiveChat topics once the LiveChat window is closed.
      livechat?.leaveAll?.()
    }
  }, [window.id, livechat])

  useInitWindowSnappingShortcuts({
    iframeRef,
    windowId: window.id,
    isFocused,
    enabled: window.canSnap && isDesktop
  })

  useListenSpeechToText(iframeRef)

  useInjectIframeScript(
    iframeRef,
    `
      window["__CONTAINER__"] = {
        parentOrigin: '${globalThis.window.location.origin}',
        windowId: '${window.id}',
        iframeId: '${iframeId}',
        title: ${JSON.stringify(window.title)},
        isMaximized: '${window.isMaximized}',
        isMinimized: '${window.isMinimized}',
        snapArea: '${window.snapArea}',
        isDetached: true,
        version: 2,
      }

      window.isDetached = true // mark as detached for the parent
      window.parentOrigin = '${globalThis.window.location.origin}'
    `
  )

  useTicketCRUDListener(iframeRef, window)

  return (
    <motion.div
      ref={windowRef}
      className={cn(styles.windowContainer, {
        [styles.maximized]: windowVariant === WindowVariant.MAXIMIZED
      })}
      variants={variants}
      transition={
        isDragging
          ? {
              duration: 0
            }
          : {
              type: 'spring',
              stiffness: 1200,
              damping: 50
            }
      }
      initial={'initial'}
      animate={windowVariant}
      exit={'closed'}
      style={{
        zIndex: isActiveWindow ? MAX_Z_INDEX + 1 : MAX_Z_INDEX,
        width: dimensions.width,
        height: dimensions.height,
        transformOrigin: dragOrigin
          ? `${dragOrigin.x * 100}% ${dragOrigin.y * 100}%`
          : undefined
      }}
      // round the translations to avoid css the blurriness bug
      transformTemplate={(_, generated) =>
        generated
          .replace(
            /translateX\((-?[\d.e+-]+)px\)/g,
            (_, v) => `translateX(${Math.round(parseFloat(v))}px)`
          )
          .replace(
            /translateY\((-?[\d.e+-]+)px\)/g,
            (_, v) => `translateY(${Math.round(parseFloat(v))}px)`
          )
      }
      drag
      dragMomentum={false}
      dragElastic={0.8}
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={{ top: topDockHeight }}
      onDragStart={ev => handleMotionDragStart(ev as PointerEvent)}
      onDrag={event => handleMotionDrag(event as PointerEvent)}
      onDragEnd={handleMotionDragEnd}
      onDragTransitionEnd={handleMotionDragEnd}
      onTransitionEnd={handleMotionTransitionEnd}
      onPointerDown={handleDragStart}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      layout
      tabIndex={0}
      onAnimationStart={() => {
        isAnimating.current = true
      }}
      onAnimationComplete={() => {
        isAnimating.current = false
      }}
    >
      <motion.div
        className={styles.window}
        layout='size'
        transition={{
          type: 'spring',
          stiffness: 900,
          damping: 50
        }}
        animate={{
          borderRadius:
            windowVariant === WindowVariant.MAXIMIZED ? '0px' : '1rem'
        }}
      >
        <div
          className={styles.titleBar}
          onPointerDown={handleTitleBarMouseDown}
          onDoubleClick={() => {
            if (window.canMaximize && isDesktop) {
              handleMaximize()
            }
          }}
        >
          <div className={styles.titleContent}>
            {window.icon && (
              <span className={styles.titleIcon}>
                <img src={window.icon} width={24} height={24} />
              </span>
            )}

            <span className={styles.titleText}>{window.title}</span>

            <NavigationControls
              isIframe={isIframe}
              onBack={handleBack}
              onForward={handleForward}
              hasBack={hasBack}
              hasForward={hasForward}
            />
          </div>
          <WindowControls
            window={window}
            isDesktop={isDesktop}
            onMinimize={handleMinimize}
            onMaximize={handleMaximize}
            onReload={handleReload}
            onClose={handleClose}
          />
        </div>
        <WindowContent
          window={window}
          iframeRef={iframeRef}
          src={iframeSrc}
          isDragging={isDragging}
          onFocusWindow={() => setActiveWindow(window.id)}
        />
      </motion.div>

      {window.canSnap && isDesktop && (
        <SnapOverlay rect={snapPreviewRect} isDragging={isDragging} />
      )}
    </motion.div>
  )
}

export { Window }
